import React from 'react'
import { store } from '../../store'
import { t } from '../../config/i18n'
import { routes } from '../../config/routes'
import { Form, Input, Button, Card, Select, Checkbox } from 'antd'
import { UnorderedListOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react'
import { useOnMount } from '../../hooks/useOnMount'
import { useCatalog } from '../../hooks/useCatalog'
import { PageSettings } from '../shared/ui/PageSettings'
import { MultiInput } from '../shared/ui/MultiInput'
import { useNavigate } from '../../hooks/useNavigate'
import { useParams } from 'react-router'
import { PERMISSIONS } from '../../models/AbstractCatalogConfig'
import { TExtraField } from '../../models/ExtraField'
import { PageTitleButton } from '../shared/ui/page/TitleButton'
import { createInitialConfigFormValues, hasAccess } from '../../lib/utilsCatalogs'
import { ruleExtraId, ruleRequiredOneOf } from '../../lib/utilsForm'
import { InputCatalogACL } from '../shared/ui/InputCatalogACL'
import { ExtraFieldsSettings } from '../shared/ui/ExtraFieldsSettings'
import { entityTypes } from '../../models/DynamicCatalogConfig'
import styles from './PageCatalogConfig.module.scss'

export const PageCatalogConfig = observer(() => {
  const navigate = useNavigate()
  const { type, id: catalogId } = useParams()
  const { catalogService, texts } = useCatalog(type, 'catalog_config')
  const [showForm, setShowForm] = React.useState(false)

  useOnMount(() => {
    catalogService.editConfigItemById(catalogId || '')
      .then(() => setShowForm(true))
      .catch(window.logger.error)
  })

  const item = catalogService.store.configItemDetails
  const [fields, setFields] = React.useState<TExtraField[]>([])
  const title = item.id ? t.catalog.edit : t.catalog.create

  React.useEffect(() => {
    setFields(item.fields)
  }, [item])

  const onSubmit = async (values: TObjectAny) => {
    if (await catalogService.saveConfigItem({ ...item, ...values, fields })) {
      catalogService.loadConfigItems(true).catch(window.logger.error)
      navigate(routes.catalogList, { type })
    }
  }

  const initialValues = createInitialConfigFormValues(item, catalogId === 'new')
  const disabled = !hasAccess(item, PERMISSIONS.CATALOG_UPDATE)

  return (
    <PageSettings
      loading={store.ui.loading}
      title={title}
      titleActions={<PageTitleButton icon={<UnorderedListOutlined />} onCLick={() => navigate(routes.catalogItemList, { type, id: catalogId, parentId: 0 })} />}
    >
      <Card>
        {!store.ui.loading && showForm && (
          <Form
            key={item.id}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 8 }}
            initialValues={initialValues}
            autoComplete="off"
            disabled={disabled}
            onFinish={onSubmit}
          >
            <Form.Item
              label={t.common.name}
              name="name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Extra ID"
              name="extraId"
              rules={[{ required: true }, ruleExtraId]}
            >
              <Input />
            </Form.Item>

            {catalogService.type === 'dynamic' && (
              <Form.Item
                label={t.catalog.entityField}
                name="entityList"
              >
                <Select
                  allowClear
                  mode="multiple"
                  options={entityOptions}
                />
              </Form.Item>
            )}

            <Form.Item
              label={t.catalog.bIdListField}
              name="businessIdList"
              rules={[
                ruleRequiredOneOf(['businessIdList', 'networkIdList'], t.catalog.errorBidNid),
              ]}
            >
              <MultiInput
                disabled={disabled}
                placeholder={'business ID'}
                validate={v => !!v.match(/^[0-9]+$/)}
              />
            </Form.Item>

            <Form.Item
              label={t.catalog.nIdListField}
              name="networkIdList"
              rules={[
                ruleRequiredOneOf(['businessIdList', 'networkIdList'], t.catalog.errorBidNid),
              ]}
            >
              <MultiInput
                disabled={disabled}
                placeholder={'network ID'}
                validate={v => !!v.match(/^[0-9]+$/)}
              />
            </Form.Item>

            <Form.Item
              label={t.catalog.aclField}
              name="acl"
            >
              <InputCatalogACL hideTable disabled={disabled} />
            </Form.Item>

            <Form.Item
              name="acl"
              wrapperCol={{ offset: 4, span: 20 }}
              style={{ margin: '-20px 0 40px 0' }}
            >
              <InputCatalogACL hideInput disabled={disabled} />
            </Form.Item>

            {catalogService.type === 'dynamic' && (
              <Form.Item
                name="saveToEntity"
                label="saveToEntity"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            )}

            <Form.Item label={t.catalog.fieldSettings} wrapperCol={{ offset: 0, span: 20 }}>
              <ExtraFieldsSettings
                className={styles.extraFields}
                fields={fields}
                onChange={setFields}
              />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 4, span: 8 }}>
              <Button type="primary" htmlType="submit">
                {t.common.save}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </PageSettings>
  )
})

const entityOptions = entityTypes.map(i => ({ value: i, label: i }))
